import React, { useCallback, useState } from 'react';
import { Text, ActionIcon, Menu, Flex } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { TableHeaderWrapper } from '../TableHeaderWrapper';
import { ColumnFilterMenu } from '../ColumnFilterMenu';
import { SuggestFilterHeader } from './SuggestFilterHeader';

interface SuggestProps {
    readonly label: string;
    readonly query: any;
    readonly optionExtractor: (data: any) => string[];
}

interface GenericFilterHeaderProps {
    readonly label: string;
    readonly filterOptions?: string[];
    readonly currentValue: string[] | null;
    readonly onChange: (value: string[] | null) => void;
    readonly customMenu?: React.ReactNode;
    readonly suggestProps?: SuggestProps;
    readonly displayValue?: string[] | null;
    readonly onOpen?: () => void;
    readonly loading?: boolean;
}

export function GenericFilterHeader(props: Readonly<GenericFilterHeaderProps>) {
    const {
        label,
        filterOptions,
        currentValue,
        onChange,
        customMenu,
        suggestProps,
        displayValue,
        onOpen,
        loading,
    } = props;
    const [isOpen, setIsOpen] = useState(false);

    const handleMenuChange = useCallback(
        (opened: boolean) => {
            setIsOpen(opened);
            if (opened) {
                onOpen?.();
            }
        },
        [onOpen],
    );

    if (suggestProps) {
        return (
            <SuggestFilterHeader
                label={suggestProps.label}
                query={suggestProps.query}
                optionExtractor={suggestProps.optionExtractor}
                currentValue={currentValue}
                onChange={onChange}
            />
        );
    }

    if (customMenu) {
        return customMenu;
    }

    const isFilterActive = (displayValue ?? currentValue ?? []).length > 0;

    return (
        <TableHeaderWrapper>
            <Menu
                closeOnItemClick={false}
                shadow="md"
                width={350}
                opened={isOpen}
                onChange={handleMenuChange}
                position="bottom-start"
            >
                <Menu.Target>
                    <Flex align="center">
                        <Text span>{label}</Text>
                        <ActionIcon
                            variant="transparent"
                            onClick={e => {
                                e.stopPropagation();
                                handleMenuChange(!isOpen);
                            }}
                            className="flex-shrink-0"
                        >
                            <IconFilter
                                size={16}
                                color={isFilterActive ? '#00539f' : 'gray'}
                            />
                        </ActionIcon>
                    </Flex>
                </Menu.Target>
                <Menu.Dropdown>
                    <ColumnFilterMenu
                        filterOptions={filterOptions ?? []}
                        currentValue={displayValue ?? currentValue ?? []}
                        onChange={onChange}
                        setIsOpen={setIsOpen}
                        loading={loading}
                    />
                </Menu.Dropdown>
            </Menu>
        </TableHeaderWrapper>
    );
}
